import { useState } from 'react';
import { LandInspection as InspectionReport, MediaItem } from '@property/shared';
import { Trees, Droplets, Waves, Trash2, Construction, Route, Maximize2, Play } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SafeImage } from '@/components/ui/safe-image';

interface SiteConditionsGridProps {
  report: InspectionReport;
  onMediaSelect: (item: MediaItem) => void;
}

const CARD_CHROME =
  'border border-border-strong/70 bg-card shadow-[0_12px_28px_-22px_rgba(29,43,34,0.5)]';

export function SiteConditionsGrid({ report, onMediaSelect }: SiteConditionsGridProps) {
  const [failedMediaIds, setFailedMediaIds] = useState<Set<string>>(new Set());

  const handleImageError = (id: string) => {
    setFailedMediaIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const conditions = [
    {
      id: 'waste',
      label: 'Waste Dumping',
      value: report.wasteDumping.value,
      icon: Trash2,
      media: report.wasteDumping.media,
    },
    {
      id: 'drain',
      label: 'Drainage',
      value: report.drainStatus.value,
      icon: Droplets,
      media: report.drainStatus.media,
    },
    {
      id: 'veg',
      label: 'Vegetation',
      value: report.vegetationStatus.value,
      icon: Trees,
      media: report.vegetationStatus.media,
    },
    {
      id: 'water',
      label: 'Waterlogging',
      value: report.waterloggedStatus.value,
      icon: Waves,
      media: report.waterloggedStatus.media,
    },
    {
      id: 'road',
      label: 'Road Access',
      value: `${report.roadCondition.type} (${report.roadCondition.rating}/5)`,
      icon: Route,
      media: undefined,
    },
    {
      id: 'const',
      label: 'Construction',
      value: report.constructionActivity.value,
      icon: Construction,
      media: report.constructionActivity.media,
    },
  ];

  // Conditions with photos get visual cards; the rest collapse into a compact
  // ledger so the grid never shows large empty placeholders.
  const withMedia = conditions.filter((c) => c.media && c.media.length > 0);
  const withoutMedia = conditions.filter((c) => !c.media || c.media.length === 0);

  return (
    <div className="space-y-4 pt-2">
      <div className="flex items-baseline justify-between gap-3 px-1">
        <h2 className="font-display text-xl font-medium tracking-tight text-foreground">
          Environmental &amp; Site Condition
        </h2>
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-foreground/45">
          6-point checklist
        </p>
      </div>

      {withMedia.length > 0 && (
        <div
          className={cn(
            'grid gap-5',
            withMedia.length === 1 ? 'sm:max-w-sm' : 'sm:grid-cols-2',
            withMedia.length >= 3 && 'md:grid-cols-3',
          )}
        >
          {withMedia.map((condition) => {
            const firstMedia = condition.media![0];
            const broken = failedMediaIds.has(firstMedia.id);
            return (
              <div key={condition.id} className={cn('group overflow-hidden', CARD_CHROME)}>
                <div
                  className={cn(
                    'relative aspect-[4/3] overflow-hidden',
                    !broken ? 'cursor-zoom-in' : 'grayscale',
                  )}
                  onClick={() => !broken && onMediaSelect(firstMedia)}
                >
                  {firstMedia.type === 'video' ? (
                    <video
                      src={firstMedia.url || ''}
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      muted
                      preload="metadata"
                    />
                  ) : (
                    <SafeImage
                      src={firstMedia.url || ''}
                      alt={condition.label}
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onErrorStateChange={(err) => err && handleImageError(firstMedia.id)}
                    />
                  )}
                  {!broken && (
                    <>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                      {firstMedia.type === 'video' && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="rounded-full bg-black/40 p-3 backdrop-blur-sm">
                            {/* eslint-disable-next-line local/no-raw-colors -- icon on dark video overlay */}
                            <Play className="h-6 w-6 text-white fill-white" />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="space-y-1.5 p-5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-foreground/45">
                    {condition.label}
                  </p>
                  <p className="font-display text-lg font-medium tracking-tight text-foreground">
                    {condition.value}
                  </p>
                  {condition.media!.length > 1 && (
                    <button
                      className="mt-2 flex items-center gap-1 text-[11px] font-semibold text-ring hover:underline underline-offset-4"
                      onClick={() => onMediaSelect(firstMedia)}
                    >
                      <Maximize2 className="size-3" /> View all {condition.media!.length} files
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {withoutMedia.length > 0 && (
        <div className={cn('divide-y divide-dashed divide-line', CARD_CHROME)}>
          {withoutMedia.map((condition) => (
            <div key={condition.id} className="flex items-center justify-between gap-4 px-5 py-3.5">
              <div className="flex items-center gap-3">
                <condition.icon className="size-4 shrink-0 text-sidebar-primary/70" />
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-foreground/45">
                  {condition.label}
                </p>
              </div>
              <p className="text-right font-display text-base font-medium tracking-tight text-foreground">
                {condition.value}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
